/**
 * Content Slide Component
 * Reusable content slide that renders bullet points with various formatting options
 */

import React from 'react';
import { SlideContainer } from '../shared/SlideContainer';
import { ContentSlideData, NavigationProps } from '../../types/slideTypes';
import { renderContentText, renderSubItems } from '../../utils/contentRenderers';

interface ContentSlideProps extends NavigationProps {
  data: ContentSlideData;
}

export const ContentSlide: React.FC<ContentSlideProps> = ({
  data,
  currentSlide,
  totalSlides,
  onPrevious,
  onNext,
  onGoToSlide,
}) => {
  return (
    <SlideContainer
      title={data.title}
      currentSlide={currentSlide}
      totalSlides={totalSlides}
      onPrevious={onPrevious}
      onNext={onNext}
      onGoToSlide={onGoToSlide}
    >
      {/* Render content items */}
      {data.content.map((item, index) => (
        <div key={index} className="space-y-3">
          {/* Section Header */}
          {item.isSectionHeader ? (
            <div className="mt-3 mb-2">
              <h3 className="text-lg font-bold text-slate-700">{item.text}</h3>
              {item.footnote && (
                <p className="text-xs text-slate-500 mt-1">{item.footnote}</p>
              )}
            </div>
          ) : item.isSubheading ? (
            /* Subheading */
            <div className="mt-6 mb-4">
              <h2 className="text-xl font-bold text-slate-800 border-b-2 border-blue-600 pb-2">
                {item.subheading}
              </h2>
            </div>
          ) : (
            /* Regular Content Item */
            <div className="flex items-start space-x-2 md:space-x-3">
              <div
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: '#1e293b', marginTop: '0.65rem' }}
              ></div>
              <div className="flex-1">
                <p
                  className="text-slate-700 leading-relaxed font-medium"
                  style={{ fontSize: 'clamp(1rem, 2vw, 1.25rem)' }}
                >
                  {renderContentText(item)}
                </p>
                {item.subItems && item.subItems.length > 0 && renderSubItems(item.subItems)}
              </div>
            </div>
          )}
        </div>
      ))}
    </SlideContainer>
  );
};
